import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { getMyProfile, signInToAdminPanel, signOutUser } from '../lib/auth';
import { insertAuditLog } from '../lib/panel';

type UserProfile = {
  id: string;
  full_name: string | null;
  email: string | null;
  role_code: string;
  is_active: boolean;
  can_access_admin_panel: boolean;
  can_manage_users: boolean;
  can_manage_roles: boolean;
  can_manage_content: boolean;
  can_manage_media: boolean;
  can_manage_pages: boolean;
  can_use_infaz_calculator: boolean;
  can_use_qr_creator: boolean;
  can_use_client_tracking: boolean;
  notes: string | null;
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Update last_seen for a user in the profiles table */
async function updateLastSeen(userId: string) {
  await supabase.rpc('touch_profile_presence', {
    p_user_id: userId,
    p_online: true,
  });
}

/** Write an audit log entry directly */
async function writeAuditLog(payload: {
  action_code: string;
  actor_email?: string | null;
  target_id?: string;
  detail?: Record<string, unknown>;
}) {
  await insertAuditLog({
    action_code: payload.action_code,
    target_table: 'profiles',
    target_id: payload.target_id,
    actor_email: payload.actor_email ?? null,
    detail: payload.detail ?? {},
  });
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  const startHeartbeat = (userId: string) => {
    stopHeartbeat();
    
    // Check if 6 hours have elapsed since login
    const sessionLoginTimeStr = localStorage.getItem('session_login_time');
    if (sessionLoginTimeStr) {
      const loginTime = parseInt(sessionLoginTimeStr, 10);
      const sixHoursMs = 6 * 60 * 60 * 1000;
      
      if (Date.now() - loginTime > sixHoursMs) {
        // Force logout if elapsed time is greater than 6 hours
        logout();
        return;
      }
    }

    // Update last_seen immediately, then every 90 seconds
    updateLastSeen(userId);
    heartbeatRef.current = setInterval(() => {
      // Re-verify the 6 hours timeout on every beat
      const currentLoginTimeStr = localStorage.getItem('session_login_time');
      if (currentLoginTimeStr) {
        const currentLoginTime = parseInt(currentLoginTimeStr, 10);
        const sixHoursMs = 6 * 60 * 60 * 1000;
        
        if (Date.now() - currentLoginTime > sixHoursMs) {
          logout();
          return;
        }
      }
      updateLastSeen(userId);
    }, 90_000);
  };

  const loadProfile = async (targetUser?: User | null) => {
    const userToLoad = targetUser ?? user;

    if (!userToLoad) {
      setProfile(null);
      return;
    }

    const { data, error } = await getMyProfile();

    if (error || !data) {
      console.error('Profil yüklenemedi:', error);
      setProfile(null);
      return;
    }

    setProfile(data as UserProfile);
  };

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);

      const {
        data: { session: initialSession },
      } = await supabase.auth.getSession();

      setSession(initialSession);
      setUser(initialSession?.user ?? null);

      if (initialSession?.user) {
        await loadProfile(initialSession.user);
        startHeartbeat(initialSession.user.id);
      } else {
        setProfile(null);
      }

      setLoading(false);
    };

    bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Skip token refresh events (triggered when tab regains focus)
      if (event === 'TOKEN_REFRESHED') return;
      // Skip INITIAL_SESSION since it's handled by bootstrap above
      if (event === 'INITIAL_SESSION') return;

      if (!newSession?.user) {
        setSession(null);
        setUser(null);
        setProfile(null);
        stopHeartbeat();
        return;
      }

      // Only update session/user synchronously, then silently refresh profile
      setSession(newSession);
      setUser(newSession.user);

      loadProfile(newSession.user);
    });

    return () => {
      subscription.unsubscribe();
      stopHeartbeat();
    };
  }, []);

  const login = async (email: string, password: string) => {
    const { data, error } = await signInToAdminPanel(email, password);

    if (error) {
      return { error: error.message || 'Giris islemi basarisiz oldu.' };
    }

    if (!data?.ok || !data.session?.access_token || !data.session?.refresh_token) {
      return {
        error:
          data?.error ||
          'Giris istegi tamamlanamadi. Lutfen tekrar deneyin.',
      };
    }

    const { error: setSessionError } = await supabase.auth.setSession({
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token,
    });

    if (setSessionError) {
      return { error: setSessionError.message };
    }

    const { data: { user: loggedInUser } } = await supabase.auth.getUser();
    if (loggedInUser) {
      localStorage.setItem('session_login_time', Date.now().toString());
      await updateLastSeen(loggedInUser.id);
      startHeartbeat(loggedInUser.id);
    }

    return { error: null };
  };

  const logout = async () => {
    // Audit log before signing out
    if (user) {
      const now = new Date();
      await writeAuditLog({
        action_code: 'user_logout',
        actor_email: user.email,
        target_id: user.id,
        detail: {
          email: user.email,
          logout_time: now.toISOString(),
        },
      });
      // Clear last_seen so they appear offline immediately.
      await supabase.rpc('touch_profile_presence', {
        p_user_id: user.id,
        p_online: false,
      });
    }
    stopHeartbeat();
    localStorage.removeItem('session_login_time');
    
    await signOutUser();
    setProfile(null);
  };

  const refreshProfile = async () => {
    await loadProfile();
  };

  const value = useMemo(
    () => ({
      user,
      session,
      profile,
      loading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshProfile,
    }),
    [user, session, profile, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}
